import { Box, Grid, GridItem } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SingleValue } from 'chakra-react-select';
import { type FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftDatePicker, {
	getTodayInNYTimezone,
} from '@/components/Molecules/FaceliftDatePicker';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import { BgProcessModule, useCreateBgProcess } from '@/hooks/service-hooks/bgProcess';
import { useGetPhysicians } from '@/hooks/service-hooks/contactsAccounts';
import {
	useGetGraftsProductSizes,
	useGetUnAssignedTissueIds,
} from '@/hooks/service-hooks/graftsToAssign';
import { useGetPatientsAi } from '@/hooks/service-hooks/patients';
import { useGetUtilityProducts } from '@/hooks/service-hooks/products';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import type { Physician } from '@/services/contactsAccounts';
import { useAuthStore, useClinicStore } from '@/store';
import type { IUnassignedTissue } from '@/types/graftsToAssigned';
import type { Patients } from '@/types/patient';
import { formatDateInUTC, formatDateToString } from '@/utils/dateUtils';

interface IUtilityFormProps {
	mode: 'create';
	initialData?: IUnassignedTissue | any;
	onSuccess?: () => void | Promise<void>;
	isLoading?: boolean;
	onIsDirtyChange?: (isDirty: boolean) => void;
}

const utilitySchema = yup.object().shape({
	Clinic: yup.object<SelectItem>().required('Clinic is required'),
	Physician: yup.object<SelectItem>().required('Provider is required'),
	Patient: yup.object<SelectItem>().required('Patient is required'),
	Product: yup.object<SelectItem>().required('Product is required'),
	DateofService: yup.string().required('Date of Service is required'),
	TissueID: yup.object<SelectItem>().required('Tissue ID is required'),
	EMRID: yup.string().required('EMR ID is required'),
	Size: yup.object<SelectItem>().required('Size is required'),
});
type UtilityFormData = yup.InferType<typeof utilitySchema>;

const UtilityForm: FC<IUtilityFormProps> = ({
	mode,
	initialData,
	onSuccess,
	isLoading = false,
	onIsDirtyChange,
}) => {

	const defaultDateOfService = formatDateToString(getTodayInNYTimezone());

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		getValues,
		watch,
		reset,
	} = useForm<UtilityFormData>({
		resolver: yupResolver(utilitySchema),
		mode: 'onSubmit',
		defaultValues: {
			Clinic: undefined,
			Physician: undefined,
			Patient: undefined,
			Product: undefined,
			Size: undefined,
			TissueID: undefined,
			EMRID: '',
			DateofService: defaultDateOfService,
		},
	});

	const user = useAuthStore((state) => state.user);
	const userId = user?.userInformation?._id || null;
	const allClinics = useClinicStore((state) => state.currentClinics);

	// Reset form when data changes
	useEffect(() => {
		if (mode === 'create') {
			reset(
				{
					Clinic: undefined,
					Physician: undefined,
					Patient: undefined,
					Product: undefined,
					Size: undefined,
					TissueID: undefined,
					EMRID: '',
					DateofService: defaultDateOfService,
				},
				{ keepDefaultValues: false, keepDirty: false },
			);
			if (onIsDirtyChange) {
				onIsDirtyChange(true);
			}
		} else if (initialData && Object.keys(initialData).length > 0) {
			const formattedData = {
				Clinic: {
					label: initialData.clinic,
					value: initialData.clinic,
				},
				Physician: {
					label: initialData.physician,
					value: initialData.physician,
				},
				Patient: {
					label: initialData.patient,
					value: initialData.patient,
				},
				Product: {
					label: initialData.product,
					value: initialData.product,
				},
				Size: {
					label: initialData.size,
					value: initialData.size,
				},
				TissueID: {
					label: initialData.tissueid,
					value: initialData.tissueid,
				},
				EMRID: initialData.emrid,
				DateofService: initialData.proceduredate,
			};
			reset(formattedData, { keepDefaultValues: false, keepDirty: false });
			if (onIsDirtyChange) {
				onIsDirtyChange(true);
			}
		}
	}, [mode, initialData, reset]);

	useEffect(() => {
		if (onIsDirtyChange) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty]);

	// Handler to prevent multiple consecutive spaces
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const input = e.currentTarget;
		const value = input.value;
		const cursorPosition = input.selectionStart || 0;

		// Prevent multiple spaces
		if (e.key === ' ') {
			// Check if previous character is already a space
			if (cursorPosition > 0 && value[cursorPosition - 1] === ' ') {
				e.preventDefault();
			}
		}
	};

	const {
		mutate: createUnAssignedGraft,
		isLoading: isCreateLoadingLog,
		isSuccess,
		isError,
	} = useCreateBgProcess(() => {
		setTimeout(() => {
			onSuccess?.();
		}, 2000);
	});

	const handleSubmitForm = handleSubmit((data) => {
		const payload = {
			GraftLogsID: 0,
			GraftLogsType: 'Assign',
			Type: 'Graftlogs',
			Clinic: (getValues('Clinic') as SelectItem)?.label,
			Physician: (getValues('Physician') as SelectItem)?.label,
			Patient: (getValues('Patient') as SelectItem)?.label,
			Product: (getValues('Product') as SelectItem)?.label,
			Size: (getValues('Size') as SelectItem)?.label,
			TissueID: (getValues('TissueID') as SelectItem)?.label,
			EMRID: getValues('EMRID'),
			DateofService: formatDateInUTC(getValues('DateofService')),
			ta_originationsource: '953870002',
		};
		createUnAssignedGraft({
      moduleName: BgProcessModule.UTILITIES_ASSIGN_GRAFTS,
      userId: userId || '', 
			clinicId: allClinics.find((item) => item.name.trim().toLowerCase() === (getValues('Clinic') as SelectItem)?.label.trim().toLowerCase())?.accountid || '',
      payload: { 
				payload, 
				additionalPayload: {
					clinicName: (getValues('Clinic') as SelectItem)?.label as string,
					patientName: (getValues('Patient') as SelectItem)?.label as string,
					physicianName: (getValues('Physician') as SelectItem)?.label as string,
					productName: (getValues('Product') as SelectItem)?.label as string,
				} 
			},
    });
	});

	const clinics = useClinicStore((state) => state.currentClinics).map(
		(clinic) => ({
			label: clinic.name,
			value: clinic.accountid,
		}),
	);

	// Physicians with pagination
	const physicians = usePaginatedSelect({
		queryHook: useGetPhysicians,
		queryParams: {
			clinic_id: (watch('Clinic') as SelectItem)?.value || undefined,
			enabled: !!watch('Clinic'),
		},
		transform: (physician: Physician) => ({
			value: physician.providerId,
			label: physician.providername,
		}),
		errorMessage: 'Unable to load providers',
		resetKey: !!watch('Clinic'),
	});

	// Patients with pagination and ai
	const patients = usePaginatedSelect({
		queryHook: useGetPatientsAi,
    queryParams: {
      clinicId: (watch('Clinic') as SelectItem)?.value as string,
      enabled: !!watch('Clinic'),
    },
		transform: (patient: Patients) => ({
			value: patient.contactid,
			label: patient.fullname,
		}),
		errorMessage: 'Unable to load patients',
		resetKey: !!watch('Clinic'),
	});

	// Products
	const { data: products, isFetching } = useGetUtilityProducts({
		clinicId: ((getValues('Clinic') as SelectItem)?.value as string) || '',
		userPort: 'WoundCare+',
		enabled: true,
	});
	const utilityProducts = useMemo(() => {
		//Reason -> Since we can have multiple products with same name, but different sizes and IDs
		const uniqueBasedOnName = new Map();
		products?.items.forEach((item) => {
			if (!uniqueBasedOnName.has(item.ta_name)) {
				uniqueBasedOnName.set(item.ta_name, item);
			}
		});
		const uniqueProducts = Array.from(uniqueBasedOnName.values());
		return uniqueProducts.map((product) => ({
			label: product.ta_name,
			value: product.ta_productid,
		}));
	}, [products]);

	// Product Sizes
	const { data: productSizes, isFetching: productSizesFetching } =
		useGetGraftsProductSizes({
			product: ((getValues('Product') as SelectItem)?.label as string) || '',
			enabled: true,
		});

	//Tissues Ids
	const { data: tissueIds, isFetching: tissueIdsFetching } =
		useGetUnAssignedTissueIds({
			clinicId: ((getValues('Clinic') as SelectItem)?.label as string) || '',
			enabled: true,
		});

	return (
		<>
			<form onSubmit={handleSubmitForm}>
				<Grid
					templateColumns={{
						base: '1fr',
						md: '2fr 1fr',
					}}
					gap={4}
					px={1}
				>
					<GridItem gap={4}>
						<Grid
							templateColumns={{
								base: '1fr',
								md: '1fr 1fr',
							}}
							gap={4}
						>
							<GridItem colSpan={{ base: 1, md: 1 }}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Clinic"
									placeholder="Select Clinic"
									value={
										clinics.find(
											(item) =>
												item.value === (watch('Clinic') as SelectItem)?.value,
										) || null
									}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setValue('Clinic', newValue as SelectItem, { shouldDirty: true });
											setValue('Physician', undefined as unknown as SelectItem, { shouldDirty: false });
											setValue('Patient', undefined as unknown as SelectItem, { shouldDirty: false });
										}
									}}
									items={clinics}
									loading={false}
									error={errors.Clinic?.message}
									isSearchable
									required
								/>
							</GridItem>
							<GridItem colSpan={{ base: 1, md: 1 }}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Provider"
									placeholder="Select Provider"
									value={
										physicians.items.find(
											(item) =>
												item.value ===
												(watch('Physician') as SelectItem)?.value,
										) || null
									}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setValue('Physician', newValue, { shouldDirty: true });
										}
									}}
									items={physicians.items}
									loading={physicians.loading}
									error={errors.Physician?.message}
									onLoadMore={physicians.onLoadMore}
									isDisabled={!watch('Clinic')}
									isSearchable
									required
								/>
							</GridItem>
							<GridItem colSpan={{ base: 1, md: 1 }}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Product"
									placeholder="Select Product"
									value={
										utilityProducts.find(
											(item) =>
												item.value === (watch('Product') as SelectItem)?.value,
										) || null
									}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setValue('Product', newValue, { shouldDirty: true });
										}
									}}
									items={utilityProducts || []}
									loading={isFetching}
									error={errors.Product?.message}
									isSearchable={true}
									isDisabled={!watch('Clinic')}
									required
								/>
							</GridItem>
							<GridItem colSpan={{ base: 1, md: 1 }}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Size"
									placeholder="Select Size"
									value={
										(productSizes || [])?.find(
											(item: SelectItem) =>
												item?.label === (watch('Size') as SelectItem)?.label,
										) || null
									}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setValue('Size', newValue, { shouldDirty: true });
										}
									}}
									items={productSizes || []}
									loading={productSizesFetching}
									error={errors.Product?.message}
									isSearchable={true}
									required
									ai={false}
								/>
							</GridItem>
							<GridItem colSpan={{ base: 1, md: 1 }} mt={4}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Tissue ID"
									placeholder="Select Tissue ID"
									value={
										(tissueIds || [])?.find(
											(item: SelectItem) =>
												item?.label ===
												(watch('TissueID') as SelectItem)?.label,
										) || null
									}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setValue('TissueID', newValue, { shouldDirty: true });
										}
									}}
									items={tissueIds || []}
									loading={tissueIdsFetching}
									error={errors.TissueID?.message}
									isSearchable={true}
									required
								/>
							</GridItem>
							<GridItem colSpan={{ base: 1, md: 1 }} mt={4}>
								<FaceliftInput
									topLabel="EMR ID"
									placeholder="000-000-000"
									{...register('EMRID')}
									error={errors.EMRID?.message}
									onKeyDown={handleKeyDown}
									required
								/>
							</GridItem>
						</Grid>
					</GridItem>

					<GridItem gap={4}>
						<Box display="flex" flexDirection="column" gap={4}>
							<FaceliftSelect<SelectItem, false>
								topLabel="Patient"
								placeholder="Select Patient"
								value={watch('Patient') as SelectItem}
								onChange={(newValue: SingleValue<SelectItem>) => {
									if (newValue) {
										setValue('Patient', newValue, { shouldDirty: true });
									}
								}}
								items={patients.items}
								loading={patients.loading}
								error={errors.Patient?.message}
								onLoadMore={patients.onLoadMore}
								isDisabled={!watch('Clinic')}
								isSearchable
								required
                ai
                aiSuggestionText="Use a keyword like “Smith” to see more relevant results"
							/>
							<FaceliftDatePicker
								topLabel="DOS"
								value={watch('DateofService')}
								onChange={(date: string) => {
									const shouldMarkDirty = date !== defaultDateOfService;
									setValue('DateofService', date, { shouldDirty: shouldMarkDirty });
								}}
								placeholder="MM/DD/YYYY"
								error={errors.DateofService?.message}
								required
								minDate={getTodayInNYTimezone()} // Prevent past dates using NY timezone
								defaultToToday
							/>
						</Box>
					</GridItem>
				</Grid>
				<Box display="flex" gap={4} justifyContent="flex-end" pl={14} mt={10}>
					<FaceliftButton
						loading={isCreateLoadingLog || isLoading}
						disabled={isCreateLoadingLog || isLoading}
						type="submit"
						cursor="pointer"
						width={'44'}
					>
						Submit
					</FaceliftButton>
				</Box>
			</form>
			<FaceliftSuccessProgressModal
				open={isCreateLoadingLog || isSuccess || isError}
				isLoading={isCreateLoadingLog}
				isSucceed={isSuccess}
				hasError={isError}
				onOpenChange={() => {}}
				title="Graft log has been created successfully."
				errorMessage={isError ? 'Error creating graft log' : ''}
			/>
		</>
	);
};

export default UtilityForm;
